import type { Game } from '../../core/game';
import type { DieResult, Phase, ThrowResult } from '../../core/types';
import { triggeredSpells, triggeredSpellText } from '../spellsApi';
import { throwDetail } from '../combatApi';

/**
 * Historique des dégâts du mode debug (COMMENTS §7.3) — tampon circulaire des
 * 50 derniers lancers, alimenté par `game.on('throw')` + `game.on('apply')`.
 *
 * `throw` sert à mémoriser **quel ennemi était visé** : au moment de `apply`,
 * l'ennemi tué a déjà été remplacé par le suivant dans `state.enemy`.
 *
 * Les champs enrichis de `ThrowResult` (`crit`, `critMult`, `spellMult`,
 * `subtotal`, `flatBonus`, livrés par l'agent core en parallèle) sont lus
 * quand ils existent ; sinon on retombe sur ce que porte déjà le lancer.
 */

export const HISTORY_LIMIT = 50;

export interface HistoryDie {
  sides: number;
  face: number;
  damage: number;
  absorbed: boolean;
  isMaster: boolean;
  crit: boolean;
  poison?: number;
}

export interface HistoryEntry {
  at: number;
  phase: Phase;
  enemy: string;
  dice: HistoryDie[];
  /** Sous-total avant sort/critique (`null` si le core ne le fournit pas). */
  subtotal: number | null;
  /** Dégâts fixes ajoutés au lancer (`null` si non fourni). */
  flatBonus: number | null;
  /** Sorts déclenchés par le lancer (v0.5.0 : il peut y en avoir plusieurs). */
  spells: string[];
  /**
   * Facteurs nommés du `breakdown` au-delà de la formule historique (v0.7.0 :
   * Larbin, *Premiers*, *ENFER*…), y compris ceux que cette version ne connaît
   * pas — ils sortent alors sous leur nom brut (`ui/combatApi.ts`).
   */
  factors: string[];
  crit: boolean | null;
  critMult: number | null;
  total: number;
  dealt: number;
  killed: boolean;
}

/** Lancer enrichi tel que le core le livrera (champs optionnels ici). */
type ExtendedThrow = ThrowResult & {
  crit?: boolean;
  critMult?: number;
  spellMult?: number;
  subtotal?: number;
  flatBonus?: number;
};

const buffer: HistoryEntry[] = [];
const pending = new Map<number, { enemy: string; phase: Phase }>();
let attached = false;

function num(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

/**
 * Vue tolérante d'un `DieResult` : l'agent core déplace le critique du dé vers
 * le lancer (COMMENTS §8), `crit` peut donc disparaître de `DieResult`. On lit
 * tous les champs non garantis en optionnel pour ne dépendre d'aucune version.
 */
type LooseDie = DieResult & { crit?: boolean; poison?: number };

function mapDice(dice: readonly DieResult[]): HistoryDie[] {
  return (dice as readonly LooseDie[]).map((d) => ({
    sides: d.sides,
    face: d.face,
    damage: d.damage,
    absorbed: d.absorbed,
    isMaster: d.isMaster,
    crit: d.crit === true,
    ...(d.poison ? { poison: d.poison } : {}),
  }));
}

function push(entry: HistoryEntry): void {
  buffer.push(entry);
  if (buffer.length > HISTORY_LIMIT) buffer.splice(0, buffer.length - HISTORY_LIMIT);
}

/**
 * Branche l'historique sur la partie. Idempotent : `src/main.ts` l'appelle une
 * fois au démarrage (mode debug uniquement).
 */
export function attachDamageHistory(game: Game): void {
  if (attached) return;
  attached = true;

  game.on('throw', (r) => {
    if (r.id < 0) return;
    pending.set(r.id, { enemy: game.state.enemy?.name ?? '—', phase: game.state.phase });
    // Un lancer non appliqué (fin de combat) ne doit pas fuir indéfiniment.
    if (pending.size > HISTORY_LIMIT * 2) {
      const oldest = pending.keys().next();
      if (!oldest.done) pending.delete(oldest.value);
    }
  });

  game.on('apply', (p) => {
    const r = p.result as ExtendedThrow;
    if (!r || r.id < 0) return;
    const ctx = pending.get(r.id);
    pending.delete(r.id);
    push({
      at: Date.now(),
      phase: ctx?.phase ?? game.state.phase,
      enemy: ctx?.enemy ?? game.state.enemy?.name ?? '—',
      dice: mapDice(r.dice),
      subtotal: num(r.subtotal),
      flatBonus: num(r.flatBonus),
      spells: triggeredSpells(r).map(triggeredSpellText),
      factors: throwDetail(r)
        .extraFactors.filter((f) => f.value !== 1)
        .map((f) => `${f.label} ${f.multiplicative ? '×' : ''}${String(f.value).replace('.', ',')}`),
      crit: typeof r.crit === 'boolean' ? r.crit : (r.dice.some((d) => d.crit) || null),
      critMult: num(r.critMult),
      total: r.total,
      dealt: p.damageDealt,
      killed: p.killed,
    });
  });
}

/** Les entrées, du plus récent au plus ancien. */
export function damageHistory(): HistoryEntry[] {
  return buffer.slice().reverse();
}

export function clearDamageHistory(): void {
  buffer.length = 0;
  pending.clear();
}
